import classNames from 'classnames'
import React from 'react'
import { FlagIcon } from 'src/components/framework/icons/flag'
import {
  MaterialIcon,
  MaterialIconRef,
} from 'src/components/framework/icons/material-icon'
import { Column, Grid } from 'src/components/framework/ui'
import { SectionHeader } from 'src/components/framework/ui/section-header'
import { SectorLink } from 'src/components/framework/ui/sector-link'
import { RatioLink } from 'src/components/page/ratios/ratios'
import { useStockReportContext } from 'src/contexts/stock-report.context'
import { Colors } from 'src/shared/colors'
import { makeUnlockLink } from 'src/shared/utils'

import * as styles from './profile.module.scss'

const NullableItem: React.FC<{ title: string; data?: string | number }> = ({
  data,
  title,
}) => {
  if (!data) {
    return null
  }
  return (
    <>
      <dt>{title}</dt>
      <dd>{data}</dd>
    </>
  )
}

export const Profile: React.FC = () => {
  const { report } = useStockReportContext()
  const { profile, security } = report

  return (
    <>
      <Grid>
        <Column msm={12} ts={12} ss={8}>
          <SectionHeader>Profile Summary</SectionHeader>
          <p className={'stk-space-out-t-0'}>{profile.description}</p>
          <SectionHeader spacing={3}>Directors</SectionHeader>
          <ul
            className={classNames(
              styles.profile__directorsList,
              'stk-wrapper',
              'stk-wrapper--compact',
              'stk-space-out-b-6',
            )}
          >
            {profile.directors.map((director, index) => (
              <li className={styles.profile__directorsItem} key={index}>
                <a
                  href={`https://www.google.com/search?q=${director.name} ${security.name}`}
                  className="stk-chip"
                  target="_blank"
                  rel="noopener"
                >
                  <span className={'stk-chip__label'}>
                    {director.name}&nbsp;
                    {director.position}
                    {director.age && ` (${director.age})`}
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <Grid>
            <Column msm={12} ts={12} ss={6}>
              <dl
                className={classNames(
                  styles.profile__dataList,
                  'stk-text-small',
                )}
              >
                <dt>Last Annual</dt>
                <dd>{profile.lastAnnualDate}</dd>
                <dt>Last Interim</dt>
                <dd>{profile.lastInterimDate}</dd>
                <dt>Incorporated</dt>
                <dd>{profile.incorporatedDate}</dd>
                <NullableItem
                  title="Public Since"
                  data={profile.publicSinceDate}
                />
                <NullableItem
                  title="No. of Shareholders"
                  data={profile.numberOfShareholders}
                />
                <NullableItem
                  title="No. of Employees"
                  data={profile.numberOfEmployees}
                />
              </dl>
            </Column>
            <Column msm={12} ts={12} ss={6}>
              <dl
                className={classNames(
                  styles.profile__dataList,
                  'stk-text-small',
                )}
              >
                {profile.sector && (
                  <>
                    <dt>Sector</dt>
                    <dd>
                      <SectorLink sector={profile.sector} />
                    </dd>
                  </>
                )}
                {profile.industry && (
                  <>
                    <dt>Industry</dt>
                    <dd>
                      <SectorLink sector={profile.industry} />
                    </dd>
                  </>
                )}
                {profile.exchange && (
                  <>
                    <dt>Exchange</dt>
                    <dd>
                      <FlagIcon
                        country={profile.exchange.countryCode}
                        className={'inline-flag'}
                      />
                      {profile.exchange.name}
                    </dd>
                  </>
                )}
                <NullableItem
                  title="Shares in Issue"
                  data={profile.sharesInIssue}
                />
                <NullableItem title="Free Float" data={profile.freeFloat} />
              </dl>
            </Column>
          </Grid>
        </Column>
        <Column msm={12} ts={12} ss={4}>
          <a
            href={makeUnlockLink()}
            aria-label={`Sign up to view ${security.name} Stock Report`}
          >
            <img
              src={
                'https://www.stockopedia.com/assets/conversion/images/maps_placeholder.jpg'
              }
              className={'stk-space-out-b-3'}
              alt={`Blurred out image of a map`}
              width={532}
              height={279}
              style={{ width: '100%', height: 'auto' }}
              loading={'lazy'}
            />
          </a>
          <dl
            className={classNames(styles.profile__dataList, 'stk-text-small')}
          >
            <dt>Address</dt>
            <dd>
              <a
                href={`https://maps.google.com/maps?q=${profile.address}`}
                target="_blank"
                rel="noopener"
              >
                {profile.address}
              </a>
            </dd>
            <dt>Web</dt>
            <dd>
              <a href={profile.website} target="_blank" rel="noopener">
                {profile.website}
              </a>
            </dd>
            <dt>Phone</dt>
            <dd>
              <a href={`tel:${profile.contact.phone}`} rel="noopener">
                {profile.contact.phone}
              </a>
            </dd>
            {profile.contact.name && profile.contact.email && (
              <>
                <dt>Contact</dt>
                <dd>
                  <a href={`mailto:${profile.contact.email}`} rel="noopener">
                    {profile.contact.name}
                  </a>
                </dd>
              </>
            )}
            <dt>Auditors</dt>
            <dd>{profile.auditors}</dd>
          </dl>
        </Column>
      </Grid>
    </>
  )
}

interface EligibilityChipProps {
  label: string
  eligible: boolean
}

const EligibilityChip: React.FC<EligibilityChipProps> = ({
  label,
  eligible,
}) => {
  return (
    <span
      className={classNames('stk-chip', 'stk-chip--sm', {
        'stk-chip--success': eligible,
        'stk-chip--danger': !eligible,
      })}
    >
      <span className={'stk-chip__slot'}>
        {eligible ? (
          <MaterialIcon
            icon={MaterialIconRef.Done}
            size={12}
            color={Colors.White}
          />
        ) : (
          <MaterialIcon
            icon={MaterialIconRef.Close}
            size={12}
            color={Colors.White}
          />
        )}
      </span>
      <span className={'stk-chip__label'}>{label}</span>
    </span>
  )
}
